import React from 'react';
import { ArrowUpRight, ShieldCheck, EyeOff, Heart, Smartphone, Wifi, Sparkles } from 'lucide-react';

/**
 * PWASpotlight — a high-conversion explainer + dual install section.
 *
 * Goal (kept subtextual, never beggy):
 *   1. Make PWA listening the hero experience.
 *   2. Build trust: no tracking, no ads, no account, no app store.
 *   3. Plant the reciprocity seed → the donation banner that follows lands soft.
 */

const PWA = {
  majestic: 'https://majesticjukeboxradio.netlify.app/',
  jazz: 'https://jazzrazzmatazzradio.netlify.app/',
};

const LOGOS = {
  majestic: 'https://customer-assets.emergentagent.com/job_radio-jukebox-2/artifacts/u6n2aq57_LOGO%20MAJESTIC%20JUKEBOX%20RADIO%20OK.jpg',
  jazz: 'https://static.wixstatic.com/media/83e446_bb80cf56149c423e95f45fdca3f13591~mv2.jpg',
};

const TRUST = [
  { icon: EyeOff,      title: 'Zero tracking',  copy: 'No analytics, no cookies, no data collected. Ever.' },
  { icon: ShieldCheck, title: 'No account',     copy: 'No sign-up, no email asked. Tap and listen.' },
  { icon: Sparkles,    title: 'No ads',         copy: 'Not a single commercial. Just the records.' },
  { icon: Wifi,        title: 'Like a real app',copy: 'Add to home screen, launch full-screen, works in the background.' },
];

const PhoneFrame = ({ logo, label, href, accent = 'from-amber to-amber-dark', testId }) => (
  <a
    data-testid={testId}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group relative block transition-transform duration-500 hover:-translate-y-2"
  >
    {/* Phone outer */}
    <div className="relative mx-auto w-[230px] sm:w-[250px] aspect-[9/19] rounded-[2.4rem] bg-gradient-to-b from-zinc-800 via-zinc-900 to-black p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85),0_0_40px_-10px_rgba(245,158,11,0.25)] border border-amber/15">
      {/* Notch */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-b-2xl bg-black z-20" />
      {/* Screen */}
      <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-vinyl">
        {/* Background album art */}
        <img
          src={logo}
          alt={label}
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-vinyl/40 via-vinyl/70 to-vinyl" />
        {/* Foreground content */}
        <div className="relative h-full flex flex-col items-center justify-between py-12 px-5 text-center">
          <div className="text-[9px] tracking-[0.25em] uppercase text-cream/60 font-bold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
            On Air
          </div>

          <div className="flex flex-col items-center gap-4">
            <div className={`relative w-28 h-28 rounded-full overflow-hidden ring-4 ring-amber/30 shadow-[0_0_30px_rgba(245,158,11,0.35)]`}>
              <img src={logo} alt={label} className="w-full h-full object-cover" />
            </div>
            <div className="font-display text-base text-cream leading-tight px-2">{label}</div>
          </div>

          <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${accent} flex items-center justify-center shadow-[0_0_20px_rgba(245,158,11,0.5)]`}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-vinyl ml-0.5">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    {/* CTA below phone */}
    <div className="mt-6 flex justify-center">
      <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber text-vinyl font-body font-bold text-xs uppercase tracking-[0.18em] shadow-[0_10px_30px_-10px_rgba(245,158,11,0.6)] group-hover:scale-[1.04] transition-transform">
        Open the app <ArrowUpRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </a>
);

const PWASpotlight = () => (
  <section data-testid="pwa-spotlight" className="relative py-20 md:py-28 overflow-hidden">
    {/* Soft glow background */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/3 left-1/4 w-[40rem] h-[40rem] rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgba(245,158,11,0.10), transparent)' }} />
      <div className="absolute bottom-0 right-1/4 w-[35rem] h-[35rem] rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgba(159,18,57,0.10), transparent)' }} />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      {/* Headline */}
      <div className="max-w-3xl mx-auto text-center mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber/30 bg-amber/5 mb-5">
          <Smartphone className="w-3.5 h-3.5 text-amber" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold">
            The best way to listen
          </span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream leading-[1.05]">
          Listen on your phone, <br className="hidden md:block" />
          <span className="font-serif-italic italic text-amber">your way.</span>
        </h2>
        <p className="mt-6 text-cream/70 font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          We built two free Progressive Web Apps so you can take the station with you —
          on the bus, in the car, while you cook. One tap, no app store, no permissions.
          They feel like a real radio app, because they are one.
        </p>
      </div>

      {/* Two phones side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-6 mb-20 max-w-3xl mx-auto">
        <PhoneFrame
          logo={LOGOS.majestic}
          label="Majestic Jukebox Radio"
          href={PWA.majestic}
          testId="pwa-spotlight-majestic"
        />
        <PhoneFrame
          logo={LOGOS.jazz}
          label="Jazz Razzmatazz Radio"
          href={PWA.jazz}
          testId="pwa-spotlight-jazz"
        />
      </div>

      {/* Trust pillars */}
      <div className="relative rounded-3xl border border-amber/15 bg-espresso/50 backdrop-blur-sm p-8 md:p-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {TRUST.map((t, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="inline-flex w-11 h-11 rounded-full bg-amber/10 border border-amber/20 items-center justify-center mb-3 md:mb-4">
                <t.icon className="w-5 h-5 text-amber" />
              </div>
              <div className="font-display text-lg text-cream leading-tight">{t.title}</div>
              <div className="mt-1.5 text-xs sm:text-sm text-cream/60 font-body leading-relaxed">{t.copy}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How-to install — 3 micro steps */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {[
          { n: '01', title: 'Open the app',     copy: 'Tap the "Open the app" button above. It launches the PWA in your browser.' },
          { n: '02', title: 'Add to Home',      copy: 'Use your browser menu → "Add to Home Screen" (iOS · Android · Desktop).' },
          { n: '03', title: 'Press play',       copy: 'Launch like any app. Sound runs in the background while you do anything else.' },
        ].map((s) => (
          <div key={s.n} className="relative pl-12">
            <div className="absolute top-0 left-0 font-display text-3xl text-amber/60">{s.n}</div>
            <div className="font-display text-xl text-cream">{s.title}</div>
            <p className="mt-2 text-sm text-cream/60 font-body leading-relaxed">{s.copy}</p>
          </div>
        ))}
      </div>

      {/* Soft bridge to donation — reciprocity nudge, never beggy */}
      <div className="mt-16 max-w-3xl mx-auto text-center">
        <Heart className="w-5 h-5 text-amber mx-auto mb-4" fill="currentColor" />
        <p className="font-serif-italic italic text-cream/80 text-lg md:text-xl leading-relaxed">
          “Free music, no advertising, no tracking — the way radio was always meant to be.
          On air since December 2017, kept alive by listeners with a kind heart.”
        </p>
      </div>
    </div>
  </section>
);

export default PWASpotlight;
